import { useState } from 'react'
import { ICampaignConfiguration } from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import { FlowIcon, TFlowIcon } from '../ui/FlowIcon'
import {
   CONTENT_SECTIONS,
   ContentSection,
   poolName,
   WorkspaceSection,
} from './model'

export interface WorkspaceLocation {
   section: WorkspaceSection
   poolId?: string
   content?: ContentSection
}
export const Sidebar = ({
   config,
   labels,
   location,
   onGo,
   open,
}: {
   config: ICampaignConfiguration
   labels: Record<number, string>
   location: WorkspaceLocation
   onGo: (next: WorkspaceLocation) => void
   open: boolean
}) => {
   const [expanded, setExpanded] = useState<string[]>(
      [location.poolId || config.contentPools[0]?.id].filter(Boolean)
   )
   const [contentOpen, setContentOpen] = useState(true)
   const row = (section: WorkspaceSection, label: string, icon: TFlowIcon) => (
      <button
         type="button"
         className="cw-nav-row"
         aria-current={location.section === section ? 'page' : undefined}
         onClick={() => onGo({ section })}
         data-testid={`nav-${section}`}
      >
         <FlowIcon name={icon} size={17} />
         <span>{label}</span>
      </button>
   )
   return (
      <nav
         aria-label="Campaign configuration"
         className={`cw-sidebar ${open ? 'is-open' : ''}`}
      >
         {row('overview', 'Overview', 'home')}
         {row('audience', 'Audience', 'users')}
         {row('cadence', 'Cadence', 'cal')}
         <button
            type="button"
            className="cw-nav-row"
            aria-expanded={contentOpen}
            onClick={() => {
               setContentOpen(location.section !== 'content' || !contentOpen)
               onGo({ section: 'content' })
            }}
            data-testid="nav-content"
         >
            <FlowIcon name="chat" size={17} />
            <span className="flex-1">Content</span>
            <FlowIcon name={contentOpen ? 'chevDown' : 'chev'} size={14} />
         </button>
         {contentOpen && (
            <div className="cw-subnav">
               {config.businessTypeIds.map((category) => (
                  <div key={category}>
                     {config.businessTypeIds.length > 1 && (
                        <p className="cw-category">
                           {labels[category] || `Category ${category}`}
                        </p>
                     )}
                     {config.contentPools
                        .filter((p) => p.businessTypeId === category)
                        .map((pool) => {
                           const isOpen =
                              expanded.includes(pool.id) ||
                              location.poolId === pool.id
                           return (
                              <div key={pool.id}>
                                 <button
                                    type="button"
                                    className="cw-nav-row"
                                    aria-expanded={isOpen}
                                    onClick={() => {
                                       setExpanded(
                                          isOpen
                                             ? expanded.filter(
                                                  (id) => id !== pool.id
                                               )
                                             : [...expanded, pool.id]
                                       )
                                       if (
                                          isOpen &&
                                          location.poolId === pool.id
                                       )
                                          onGo({ section: 'content' })
                                    }}
                                    data-testid={`pool-${pool.id}`}
                                 >
                                    <span className="flex-1">
                                       {poolName(config, pool, labels)}
                                    </span>
                                    {pool.isDefault && (
                                       <span className="cw-count">General</span>
                                    )}
                                    <FlowIcon
                                       name={isOpen ? 'chevDown' : 'chev'}
                                       size={12}
                                    />
                                 </button>
                                 {isOpen && (
                                    <div className="cw-subnav">
                                       {CONTENT_SECTIONS.map((item) => (
                                          <button
                                             key={item.id}
                                             type="button"
                                             className="cw-nav-row"
                                             aria-current={
                                                location.section ===
                                                   'content' &&
                                                location.poolId === pool.id &&
                                                location.content === item.id
                                                   ? 'page'
                                                   : undefined
                                             }
                                             onClick={() =>
                                                onGo({
                                                   section: 'content',
                                                   poolId: pool.id,
                                                   content: item.id,
                                                })
                                             }
                                             data-testid={`nav-${pool.id}-${item.id}`}
                                          >
                                             <FlowIcon
                                                name={item.icon}
                                                size={13}
                                             />
                                             <span className="flex-1">
                                                {item.label}
                                             </span>
                                             {item.id !== 'description' &&
                                                (pool[item.id]?.length || 0) >
                                                   0 && (
                                                   <span className="cw-count">
                                                      {pool[item.id]?.length}
                                                   </span>
                                                )}
                                          </button>
                                       ))}
                                    </div>
                                 )}
                              </div>
                           )
                        })}
                  </div>
               ))}
               <button
                  type="button"
                  className="cw-nav-row"
                  onClick={() =>
                     onGo({
                        section: config.businessTypeIds.length
                           ? 'add-type'
                           : 'audience',
                     })
                  }
                  data-testid="nav-add-type"
               >
                  <FlowIcon name="plus" size={17} />
                  <span>Add subtype</span>
               </button>
            </div>
         )}
         <div className="cw-nav-divider" />
         {row('voice', 'Voice & persona', 'voice')}
         {row('team', 'Agent routing', 'users')}
         {row('numbers', 'Phone numbers', 'phone')}
      </nav>
   )
}
